import React, {useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {showErrorToast} from '../../../shared/utils/apiErrorMessage'
import {KTSVG} from '../../../../_metronic/helpers'
import type {Vessel, VesselParticulars} from '../core/_models'
import {
  createVesselParticulars,
  getVesselParticulars,
  updateVesselParticulars,
} from '../core/_requests'
import {VESSEL_PARTICULAR_SECTIONS} from '../core/vesselParticularSections'

interface Props {
  isOpen: boolean
  isEdit: boolean
  onClose: () => void
  onVesselAdded: () => void
  vesselData: Vessel
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const buildEmptyParticulars = (vessel: Vessel): VesselParticulars => ({
  vesselId: vessel.id,
  vesselName: vessel.fleet_name,
  vesselType: vessel.vesselType,
  callSign: vessel.call_sign || '',
  flag: vessel.flag || '',
  portOfRegistry: vessel.portOfRegistry || '',
  officialNumber: vessel.officialNumber || '',
  imoNumber: vessel.imoNumber || '',
  classSociety: vessel.classes || '',
  classNotation: vessel.classNotation || '',
  pAndIClub: vessel.piClub || '',
  vesselPhoneNumber: vessel.vesselPhoneNumber || vessel.vesselPhoneNumbers?.[0] || '',
  vesselEmailAddress: vessel.vesselEmailAddress || vessel.vesselEmails?.[0] || '',
  mmsi: vessel.mmsi ? String(vessel.mmsi) : '',
})

const ShipParticularsModal: React.FC<Props> = ({
  isOpen,
  isEdit,
  onClose,
  onVesselAdded,
  vesselData,
}) => {
  const [activeTab, setActiveTab] = useState(VESSEL_PARTICULAR_SECTIONS[0].key)
  const [form, setForm] = useState<VesselParticulars>(buildEmptyParticulars(vesselData))
  const [hasExisting, setHasExisting] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!isOpen) return
    setActiveTab(VESSEL_PARTICULAR_SECTIONS[0].key)
    setLoading(true)
    getVesselParticulars(vesselData.id)
      .then((data) => {
        setHasExisting(Boolean(Object.keys(data || {}).some((key) => key !== 'vesselId' && (data as any)[key] != null && (data as any)[key] !== '')))
        setForm({...buildEmptyParticulars(vesselData), ...data})
      })
      .catch(() => {
        setHasExisting(false)
        setForm(buildEmptyParticulars(vesselData))
      })
      .finally(() => setLoading(false))
  }, [isOpen, vesselData])

  const fieldByKey = useMemo(() => {
    const map: Record<string, {type: string; readOnly?: boolean}> = {}
    VESSEL_PARTICULAR_SECTIONS.forEach((section) =>
      section.fields.forEach((field) => {
        map[String(field.key)] = {type: field.type, readOnly: field.readOnly}
      })
    )
    return map
  }, [])

  const updateField = (key: keyof VesselParticulars, rawValue: string) => {
    const field = fieldByKey[String(key)]
    let value: any = rawValue
    if (field?.type === 'number') {
      value = rawValue === '' ? null : Number(rawValue)
    }
    setForm((prev) => ({...prev, [key]: value}))
  }

  const validate = () => {
    const next: Record<string, string> = {}
    VESSEL_PARTICULAR_SECTIONS.forEach((section) =>
      section.fields.forEach((field) => {
        const value = (form as any)[field.key]
        if (field.type === 'number' && value != null && value !== '' && Number(value) < 0) {
          next[String(field.key)] = 'Must be non-negative.'
        }
      })
    )
    if (form.vesselEmailAddress && !EMAIL_RE.test(form.vesselEmailAddress)) {
      next.vesselEmailAddress = 'Enter a valid email address.'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return
    setSaving(true)
    try {
      const payload = {...form, vesselId: vesselData.id, vesselName: vesselData.fleet_name, vesselType: vesselData.vesselType}
      if (hasExisting || isEdit) {
        await updateVesselParticulars(vesselData.id, payload)
      } else {
        await createVesselParticulars(vesselData.id, payload)
        setHasExisting(true)
      }
      toast.success('Vessel Particulars saved successfully')
      onVesselAdded()
      onClose()
    } catch (error: any) {
      console.error('Failed to save Vessel Particulars:', error)
      showErrorToast(error, 'Failed to save Vessel Particulars.')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className='modal-overlay' style={{zIndex: 1050}}>
      <div className='modal-content' style={{maxWidth: '76rem', maxHeight: '92vh', overflow: 'hidden'}}>
        <div className='custom-modal-header d-flex justify-content-between align-items-center'>
          <h5 className='m-0'>
            {isEdit || hasExisting ? 'Edit Vessel Particulars' : 'Vessel Particulars'} - {vesselData.fleet_name}
          </h5>
          <button className='close-btn' onClick={onClose}>
            <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
          </button>
        </div>

        <div className='px-3 pt-2 border-bottom'>
          <ul className='nav nav-tabs flex-nowrap overflow-auto'>
            {VESSEL_PARTICULAR_SECTIONS.map((tab) => (
              <li className='nav-item' key={tab.key}>
                <button
                  type='button'
                  className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className='custom-modal-body' style={{overflowY: 'auto', maxHeight: 'calc(92vh - 180px)'}}>
          {loading ? (
            <div className='text-muted py-5 text-center'>Loading Vessel Particulars...</div>
          ) : (
            VESSEL_PARTICULAR_SECTIONS.filter((section) => section.key === activeTab).map((section) => (
              <div className='row g-3' key={section.key}>
                {section.fields.map((field) => {
                  const value = (form as any)[field.key]
                  const controlClass = field.type === 'textarea' ? 'col-12' : 'col-md-6'
                  return (
                    <div className={controlClass} key={String(field.key)}>
                      <label className='modal_label'>{field.label}{field.unit ? ` (${field.unit})` : ''}</label>
                      {field.type === 'textarea' ? (
                        <textarea
                          className='form-control'
                          rows={3}
                          value={value ?? ''}
                          disabled={field.readOnly}
                          onChange={(e) => updateField(field.key, e.target.value)}
                        />
                      ) : (
                        <input
                          className='form-control'
                          type={field.type}
                          value={value ?? ''}
                          disabled={field.readOnly}
                          onChange={(e) => updateField(field.key, e.target.value)}
                        />
                      )}
                      {errors[String(field.key)] && <small className='text-danger'>{errors[String(field.key)]}</small>}
                    </div>
                  )
                })}
              </div>
            ))
          )}
        </div>

        <div className='d-flex justify-content-end gap-2 p-3 border-top'>
          <button className='btn btn_secondary' onClick={onClose} disabled={saving}>Cancel</button>
          <button className='btn btn_success' onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save Vessel Particulars'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ShipParticularsModal
